import type { IPermissionSnapshot } from '@/authz/types';

import type { HelpStateFilter } from './help-centre.types';

/**
 * Query keys for the Help Centre.
 *
 * Every reader key carries the caller's PERMISSION SCOPE. The server filters guides by the token's
 * action claims, and the query cache outlives a logout in the same tab — so without the scope in the
 * key, a Party Admin signing in after a Super Admin would be served the Super Admin's tree from
 * cache: exactly the leak "hide entirely" exists to prevent.
 */

const hash = (input: string): string => {
	let h = 5381;
	for (let i = 0; i < input.length; i++) h = ((h << 5) + h + input.charCodeAt(i)) | 0;
	return (h >>> 0).toString(36);
};

export const permissionScopeKey = (snapshot: IPermissionSnapshot): string =>
	`${[...snapshot.roles].sort().join('|')}#${hash([...snapshot.actions].sort().join(','))}`;

export const helpKeys = {
	all: ['help-centre'] as const,
	capabilities: (scope: string) => [...helpKeys.all, 'capabilities', scope] as const,
	tree: (scope: string) => [...helpKeys.all, 'tree', scope] as const,
	article: (scope: string, slug: string) => [...helpKeys.all, 'article', scope, slug] as const,
	search: (scope: string, q: string, page: number, pageSize: number) =>
		[...helpKeys.all, 'search', scope, q, page, pageSize] as const,
	context: (scope: string, key: string) => [...helpKeys.all, 'context', scope, key] as const,
	admin: {
		all: () => [...helpKeys.all, 'admin'] as const,
		list: (filter: { moduleKey?: string; state?: HelpStateFilter; page: number; pageSize: number }) =>
			[...helpKeys.admin.all(), 'list', filter] as const,
		article: (id: number) => [...helpKeys.admin.all(), 'article', id] as const,
		versions: (id: number) => [...helpKeys.admin.all(), 'versions', id] as const,
	},
};
